import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/config";
import { PaymentBadge } from "../components/JobActionsMenu";
import AutoPayEnrollmentControl from "../components/AutoPayEnrollmentControl";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  ArrowLeft, Search, X, Calendar, ChevronDown, Loader2,
  CheckCircle2, XCircle, Repeat, DollarSign, Pencil, MessageSquare, FileText,
  CreditCard, ClipboardList, Copy, PackagePlus, Mail,
} from "lucide-react";

const FREQUENCY_LABEL = {
  one_time: "One Time", weekly: "Weekly", biweekly: "Every 2 Weeks",
  every_3_weeks: "Every 3 Weeks", monthly: "Monthly", every_6_weeks: "Every 6 Weeks",
  every_2_months: "Every 2 Months", every_3_months: "Every 3 Months",
  every_6_months: "Every 6 Months", yearly: "Once Per Year", custom: "Custom",
};

const STATUS_STYLES = {
  scheduled: "bg-blue-100 text-blue-800",
  in_progress: "bg-amber-100 text-amber-800",
  completed: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-rose-100 text-rose-800",
  canceled: "bg-rose-100 text-rose-800",
};

const PAY_STYLES = {
  paid: "bg-emerald-100 text-emerald-800",
  unpaid: "bg-gray-100 text-gray-700",
  autopay_scheduled: "bg-violet-100 text-violet-800",
  pay_on_arrival: "bg-amber-100 text-amber-800",
  payment_pending: "bg-rose-100 text-rose-800",
  payment_failed: "bg-rose-100 text-rose-800",
};

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function CustomerBookings() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | upcoming | today | completed | cancelled | payment_pending
  const [search, setSearch] = useState("");
  const [openMenu, setOpenMenu] = useState(null); // booking_id whose quick-actions menu is open
  // iter110 — inline modal state so quick actions never navigate away.
  const [activeModal, setActiveModal] = useState(null); // { kind, booking } | null
  const openModal = (kind, booking) => { setActiveModal({ kind, booking }); setOpenMenu(null); };
  const closeModal = () => setActiveModal(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/customers/${userId}/bookings`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load customer bookings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [userId]);

  // Auto-refresh so this page stays in sync with the Calendar (bidirectional).
  useEffect(() => {
    const int = setInterval(load, 30000);
    return () => clearInterval(int);
  }, [userId]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const today = todayISO();
    const q = search.trim().toLowerCase();
    return (data.bookings || []).filter((b) => {
      const status = (b.status || "").toLowerCase();
      const pay = (b.payment_status || "").toLowerCase();
      const pd = b.preferred_date || "";
      // Filter chip
      if (filter === "upcoming") {
        if (status === "completed" || status === "cancelled" || status === "canceled") return false;
        if (pd < today) return false;
      } else if (filter === "today") {
        if (pd !== today) return false;
        if (status === "cancelled" || status === "canceled") return false;
      } else if (filter === "completed" && status !== "completed") return false;
      else if (filter === "cancelled" && !(status === "cancelled" || status === "canceled")) return false;
      else if (filter === "payment_pending") {
        if (status === "cancelled" || status === "canceled" || status === "completed") return false;
        if (!["unpaid", "payment_pending", "payment_failed", "autopay_scheduled"].includes(pay)) return false;
      }
      // Search
      if (q) {
        const hay = `${b.booking_id || ""} ${b.service_name || ""} ${b.service_key || ""} ${b.address || ""} ${pd}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [data, filter, search]);

  if (loading && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-700" />
      </div>
    );
  }
  if (!data) return null;

  const s = data.summary || {};
  const c = data.customer || {};

  return (
    <div className="min-h-screen bg-gray-50 pb-16" data-testid="admin-customer-bookings-page">
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin?tab=customers")} data-testid="back-to-crm">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to CRM
          </Button>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-emerald-700">Customer Bookings</div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900 font-[Outfit] truncate">
              {c.name || "Unknown customer"}
            </h1>
            <div className="text-sm text-gray-600 truncate">
              {c.email || "no email"} {c.phone && `· ${c.phone}`}
              {c.default_address && <> · {c.default_address}</>}
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={load} className="rounded-full" data-testid="refresh-bookings">
            <Loader2 className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5" data-testid="bookings-summary-cards">
          <SummaryCard label="Total Bookings" value={s.total} testid="summary-total" />
          <SummaryCard label="Upcoming" value={s.upcoming} highlight="emerald" testid="summary-upcoming" />
          <SummaryCard label="Completed" value={s.completed} highlight="emerald" testid="summary-completed" />
          <SummaryCard label="Active Recurring" value={s.active_recurring} highlight="blue" testid="summary-active-recurring" />
          <SummaryCard label="Payment Pending" value={s.payment_pending} highlight="rose" testid="summary-payment-pending" />
        </div>

        {/* iter204 — Manual AutoPay enrollment for this customer profile.
            Owner ask (2026-03-01): the admin needs a clear "Enable AutoPay"
            button directly on the customer profile that reuses the saved
            PM from a previous invoice payment, or falls back to sending
            a fresh authorization link when Stripe says off-session is not
            legally allowed on the current PM. */}
        {c.user_id && (
          <div className="mb-5" data-testid="customer-profile-autopay">
            <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-emerald-700 mb-1.5">
              AutoPay Enrollment
            </div>
            <AutoPayEnrollmentControl
              userId={c.user_id}
              onChange={load}
              testIdPrefix="customer-profile-autopay"
            />
          </div>
        )}

        {/* Search + Filter chips */}
        <div className="bg-white rounded-2xl border border-gray-200 p-3 mb-4 shadow-sm">
          <div className="flex items-center gap-2 relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by Booking ID · Service · Address · Date"
              className="pl-9 h-11 rounded-xl"
              data-testid="bookings-search-input"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-3" data-testid="bookings-search-clear">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="bookings-filter-chips">
            {[
              { key: "all", label: `All (${s.total || 0})` },
              { key: "upcoming", label: `Upcoming (${s.upcoming || 0})` },
              { key: "today", label: `Today (${s.today || 0})` },
              { key: "completed", label: `Completed (${s.completed || 0})` },
              { key: "cancelled", label: `Cancelled (${s.cancelled || 0})` },
              { key: "payment_pending", label: `Payment Pending (${s.payment_pending || 0})` },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`filter-${f.key}`}
                className={`px-3 h-8 rounded-full text-[12px] font-extrabold border transition ${
                  filter === f.key
                    ? "bg-[#047857] text-white border-[#047857]"
                    : "bg-white text-gray-700 border-gray-200 hover:border-emerald-300"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Booking rows */}
        {filtered.length === 0 ? (
          <div className="rounded-2xl bg-white border border-gray-200 p-8 text-center text-gray-500" data-testid="bookings-empty">
            No bookings match this filter.
          </div>
        ) : (
          <ul className="space-y-3" data-testid="bookings-list">
            {filtered.map((b) => (
              <BookingRow
                key={b.booking_id}
                booking={b}
                openMenu={openMenu}
                setOpenMenu={setOpenMenu}
                onChanged={load}
                onOpenModal={openModal}
              />
            ))}
          </ul>
        )}
      </div>

      {/* iter110 — Inline modals for quick actions (no navigation-away) */}
      {activeModal && (
        <BookingActionModal
          kind={activeModal.kind}
          booking={activeModal.booking}
          customer={c}
          onClose={closeModal}
          onChanged={load}
        />
      )}
    </div>
  );
}

/* ============ Sub-components ============ */

function SummaryCard({ label, value, highlight, testid }) {
  const border = { emerald: "border-emerald-200", blue: "border-blue-200", rose: "border-rose-200" }[highlight] || "border-gray-200";
  const text = { emerald: "text-emerald-800", blue: "text-blue-800", rose: "text-rose-800" }[highlight] || "text-gray-900";
  return (
    <div className={`rounded-2xl bg-white border ${border} p-3.5 shadow-sm`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-gray-500">{label}</div>
      <div className={`mt-1 text-3xl font-extrabold tabular-nums tracking-tighter ${text}`}>{value ?? 0}</div>
    </div>
  );
}

function StatusPill({ value, styleMap }) {
  const cls = styleMap[(value || "").toLowerCase()] || "bg-gray-100 text-gray-700";
  const label = (value || "—").replace(/_/g, " ");
  return <span className={`inline-flex items-center text-[10px] font-extrabold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
}

function BookingRow({ booking, openMenu, setOpenMenu, onChanged, onOpenModal }) {
  const b = booking;
  const isMenuOpen = openMenu === b.booking_id;
  const isRecurring = b.frequency && b.frequency !== "one_time";
  const isDone = (b.status || "").toLowerCase() === "completed";
  const isCancelled = ["cancelled", "canceled"].includes((b.status || "").toLowerCase());

  return (
    <li className="rounded-2xl bg-white border border-gray-200 p-4 shadow-sm" data-testid={`booking-row-${b.booking_id}`}>
      {/* Header row */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-extrabold tracking-tight text-gray-900 font-[Outfit]">{b.service_name || b.service_key}</h3>
            <StatusPill value={b.status} styleMap={STATUS_STYLES} />
            <PaymentBadge
              paymentStatus={b.payment_status}
              bookingId={b.booking_id}
              onMarkPaid={async (id) => {
                try {
                  await api.post(`/admin/bookings/${id}/mark-paid`);
                  toast.success("Marked as Paid");
                  onChanged?.();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Could not mark as paid");
                }
              }}
            />
            {isRecurring && (
              <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-blue-50 text-blue-800 border border-blue-200">
                <Repeat className="w-3 h-3" /> {FREQUENCY_LABEL[b.frequency] || b.frequency}
              </span>
            )}
            {b.autopay_enabled && (
              <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-violet-50 text-violet-800 border border-violet-200">
                <CreditCard className="w-3 h-3" /> AutoPay
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500 mt-1 font-mono truncate">{b.booking_id}</div>
          <div className="text-sm text-gray-700 mt-1 truncate">{b.address}</div>
          {b.assigned_crew && <div className="text-xs text-gray-500 mt-0.5">Crew: {b.assigned_crew}</div>}
          {b.notes && <div className="text-xs italic text-gray-500 mt-0.5">{b.notes}</div>}
        </div>
        <div className="text-right shrink-0">
          <div className="text-xl font-extrabold tabular-nums text-gray-900">${Number(b.price || 0).toFixed(2)}</div>
          <div className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-gray-500 mt-0.5">per visit</div>
        </div>
      </div>

      {/* Schedule dates */}
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2 text-[12px]">
        {b.preferred_date && (
          <DateChip label={isDone ? "Serviced" : "Next visit"} value={b.preferred_date} testid={`date-preferred-${b.booking_id}`} />
        )}
        {b.completed_at && (
          <DateChip label="Last completed" value={b.completed_at.split("T")[0]} testid={`date-completed-${b.booking_id}`} />
        )}
        {b.preferred_time && (
          <DateChip label="Arrival" value={b.preferred_time} testid={`date-window-${b.booking_id}`} />
        )}
      </div>

      {/* Future visits — only shown when this recurring service is still active.
          Completed rows never show the phantom-dates strip; cancelled rows never do. */}
      {isRecurring && !isDone && !isCancelled && b.future_visits && b.future_visits.length > 0 && (
        <div className="mt-3 rounded-xl border border-emerald-100 bg-emerald-50/60 p-3" data-testid={`future-visits-${b.booking_id}`}>
          <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-[#047857] flex items-center gap-1">
            <Calendar className="w-3 h-3" /> Future visits ({FREQUENCY_LABEL[b.frequency] || b.frequency})
          </div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {b.future_visits.map((d) => (
              <span key={d} className="inline-flex items-center px-2 py-1 rounded-full bg-white border border-emerald-200 text-[11px] font-extrabold tabular-nums text-emerald-800">
                {d}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* iter212 — Owner ask (2026-03-01): RETRY PAYMENT must be visible
          directly on any completed + payment_pending booking card, not
          buried inside Quick actions → Payment. Shows the same uncapped
          idempotent charge as iter209 — one click from the customer
          profile. */}
      {b.status === "completed" &&
       (b.payment_status === "payment_pending" || b.payment_status === "payment_failed") && (
        <div className="mt-3">
          <InlineRetryPaymentButton booking={b} onChanged={onChanged} />
        </div>
      )}

      {/* Quick action menu */}
      <div className="mt-3 flex items-center justify-end gap-2 relative">
        <Button
          variant="outline" size="sm" className="rounded-full"
          onClick={() => setOpenMenu(isMenuOpen ? null : b.booking_id)}
          data-testid={`booking-actions-${b.booking_id}`}
        >
          Quick actions <ChevronDown className="w-3.5 h-3.5 ml-1" />
        </Button>
        {isMenuOpen && (
          <QuickActionsMenu booking={b} onDone={() => { setOpenMenu(null); onChanged(); }} onOpenModal={onOpenModal} />
        )}
      </div>
    </li>
  );
}

function InlineRetryPaymentButton({ booking, onChanged }) {
  const b = booking;
  const hasCard = !!b.customer_has_card_on_file;
  const [busy, setBusy] = useState(false);
  const retry = async () => {
    const amt = Number(b.price || 0).toFixed(2);
    if (!window.confirm(`Retry charging the customer's saved card for $${amt}?\n\nNo attempt cap — you can retry again later if this attempt fails.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bookings/${b.booking_id}/retry-payment`, {});
      if (data.ok) {
        toast.success(`Payment captured — $${amt} (Stripe PI ${data.payment_intent_id || ""})`);
      } else {
        toast.error(`Retry failed: ${data.charge_info || "Unknown error"}. You can click Retry Payment again after fixing the card.`);
      }
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-2xl border-2 border-rose-200 bg-rose-50/60 px-3 py-2 flex items-center justify-between gap-3 flex-wrap"
         data-testid={`inline-retry-payment-${b.booking_id}`}>
      <div className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-rose-800">
        Completed · payment pending
        {!hasCard && (
          <span className="ml-2 text-[10px] font-bold text-rose-600 normal-case tracking-normal">
            (no saved card — send a payment reminder)
          </span>
        )}
      </div>
      <Button
        onClick={retry}
        disabled={busy || !hasCard}
        className="rounded-full bg-rose-700 hover:bg-rose-800 text-white text-xs"
        data-testid={`inline-retry-payment-btn-${b.booking_id}`}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Retry Payment"}
      </Button>
    </div>
  );
}

function DateChip({ label, value, testid }) {
  return (
    <div className="rounded-lg bg-gray-50 border border-gray-200 px-2.5 py-1.5" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-[0.16em] font-extrabold text-gray-500">{label}</div>
      <div className="text-[13px] font-extrabold text-gray-900 tabular-nums">{value}</div>
    </div>
  );
}

function ActionRow({ icon: Icon, label, onClick, danger = false, disabled = false, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`flex items-center gap-2 w-full text-left px-3 py-2 rounded-lg text-[13px] font-extrabold transition ${
        danger ? "text-rose-700 hover:bg-rose-50" : "text-gray-700 hover:bg-emerald-50"
      } disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      <Icon className="w-4 h-4 shrink-0" /> {label}
    </button>
  );
}

function QuickActionsMenu({ booking, onDone, onOpenModal }) {
  const b = booking;
  const [busy, setBusy] = useState(false);
  const isRecurring = b.frequency && b.frequency !== "one_time";
  const isDone = (b.status || "").toLowerCase() === "completed";
  const isCancelled = ["cancelled", "canceled"].includes((b.status || "").toLowerCase());

  const post = async (path, opts = {}) => {
    setBusy(true);
    try {
      await api.post(path, opts.body || {});
      toast.success(opts.msg || "Done");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };
  const patch = async (path, body, msg) => {
    setBusy(true);
    try {
      await api.patch(path, body);
      toast.success(msg || "Saved");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  // ActionRow rendered inline below.


  const promptReschedule = async () => {
    const d = window.prompt("New date (YYYY-MM-DD):", b.preferred_date);
    if (!d) return;
    const t = window.prompt("Arrival window:", b.preferred_time || "Morning (7am-11am)") || b.preferred_time;
    await patch(`/admin/bookings/${b.booking_id}`, { preferred_date: d, preferred_time: t }, "Rescheduled");
  };
  const promptChangeFreq = async () => {
    const f = window.prompt("New frequency (weekly, biweekly, every_3_weeks, monthly, every_6_weeks, every_2_months, every_3_months, every_6_months, yearly, one_time, custom):", b.frequency);
    if (!f) return;
    await patch(`/admin/bookings/${b.booking_id}`, { frequency: f }, "Frequency changed");
  };
  const promptUpdatePrice = async () => {
    const p = window.prompt("New per-visit price ($):", b.price);
    if (!p) return;
    const n = parseFloat(p);
    if (isNaN(n) || n < 0) { toast.error("Invalid price"); return; }
    await patch(`/admin/bookings/${b.booking_id}`, { price: n }, "Price updated");
  };
  const confirmCancelOne = async () => {
    if (!window.confirm("Cancel this ONE visit?")) return;
    await post(`/admin/bookings/${b.booking_id}/cancel`, {
      body: { reason: "Admin cancelled this visit" }, msg: "Visit cancelled",
    });
  };
  const confirmCancelAll = async () => {
    // iter111 — Cancel Recurring Service is a HARD delete of future visits.
    // Completed history + invoices remain untouched.
    if (!window.confirm(
      `Cancel the ENTIRE recurring "${b.service_name || b.service_key}" service?\n\n` +
      "• All future visits for this service will be DELETED (removed from Admin Calendar, Customer Calendar, Route Schedule, and Booking List).\n" +
      "• Completed visits and invoices are kept for history.\n" +
      "• No more SMS / email / push reminders will be sent for this service.\n" +
      "• A new recurring service can be started later."
    )) return;
    if (!b.user_id) { toast.error("Missing user_id on booking"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/customers/${b.user_id}/recurring/cancel?service_key=${encodeURIComponent(b.service_key || "")}`,
        {},
      );
      const n = data?.deleted_count ?? 0;
      toast.success(`Recurring service cancelled — ${n} future visit${n === 1 ? "" : "s"} removed`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel recurring service");
    } finally { setBusy(false); }
  };
  const confirmComplete = async () => {
    if (!window.confirm("Mark this visit complete? This will auto-charge (if saved card) and create the next recurring visit.")) return;
    await post(`/admin/bookings/${b.booking_id}/complete`, { msg: "Visit marked complete" });
  };
  const confirmSkipNext = async () => {
    if (!window.confirm("Skip the next visit?")) return;
    if (!b.user_id) { toast.error("Missing user_id on booking"); return; }
    await post(`/admin/customers/${b.user_id}/recurring/skip-next`, { msg: "Next visit skipped" });
  };
  // iter110 — open inline modals instead of navigating away.
  const openView          = () => onOpenModal?.("view", b);
  const openEdit          = () => onOpenModal?.("edit", b);
  const openPayment       = () => onOpenModal?.("payment", b);
  const openInvoice       = () => onOpenModal?.("invoice", b);
  const openMessages      = () => onOpenModal?.("messages", b);
  const openAddService    = () => onOpenModal?.("add-service", b);
  const openDuplicateCheck = () => onOpenModal?.("duplicate-check", b);
  // iter112 — inline "Send Payment Reminder" quick action (bypasses the
  // Payment modal for a single-click reminder on unpaid rows).
  const isPaid = (b.payment_status || "").toLowerCase() === "paid";
  const sendPaymentReminderInline = async () => {
    if (!window.confirm(`Send a payment reminder (SMS + Email) to ${b.name || "the customer"}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bookings/${b.booking_id}/payment-reminder`, {});
      const parts = [];
      if (data.channels_sent?.length) parts.push(`sent via ${data.channels_sent.join(" + ")}`);
      if (data.channels_failed?.length) parts.push(`failed on ${data.channels_failed.join(", ")}`);
      toast.success(`Payment reminder ${parts.join(" · ") || "queued"}`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send reminder");
    } finally { setBusy(false); }
  };

  return (
    <div className="absolute right-0 top-10 z-20 w-72 rounded-2xl bg-white border border-gray-200 shadow-xl p-2 space-y-0.5" data-testid={`quick-actions-menu-${b.booking_id}`}>
      <ActionRow icon={ClipboardList} label="View"                     onClick={openView}          disabled={busy} testid={`qa-view-${b.booking_id}`} />
      <ActionRow icon={Pencil}        label="Edit"                     onClick={openEdit}          disabled={busy || isCancelled || isDone} testid={`qa-edit-${b.booking_id}`} />
      <ActionRow icon={Calendar}      label="Reschedule"               onClick={promptReschedule}  disabled={isDone || isCancelled || busy} testid={`qa-reschedule-${b.booking_id}`} />
      {isRecurring && (
        <ActionRow icon={Calendar}    label="Skip Next Visit"          onClick={confirmSkipNext}   disabled={isCancelled || busy} testid={`qa-skip-${b.booking_id}`} />
      )}
      <ActionRow icon={CheckCircle2}  label="Complete"                 onClick={confirmComplete}   disabled={isDone || isCancelled || busy} testid={`qa-complete-${b.booking_id}`} />
      <ActionRow icon={XCircle}       label="Cancel One Visit"         onClick={confirmCancelOne}  danger disabled={isCancelled || isDone || busy} testid={`qa-cancel-one-${b.booking_id}`} />
      {isRecurring && (
        <ActionRow icon={XCircle}     label="Cancel Recurring Service" onClick={confirmCancelAll}  danger disabled={busy} testid={`qa-cancel-series-${b.booking_id}`} />
      )}
      <ActionRow icon={Repeat}        label="Change Frequency"         onClick={promptChangeFreq}  disabled={isCancelled || busy} testid={`qa-change-freq-${b.booking_id}`} />
      <ActionRow icon={DollarSign}    label="Update Price"             onClick={promptUpdatePrice} disabled={isCancelled || busy} testid={`qa-update-price-${b.booking_id}`} />
      <ActionRow icon={PackagePlus}   label="Add Service"              onClick={openAddService}    disabled={busy} testid={`qa-add-service-${b.booking_id}`} />
      {!isPaid && (
        <ActionRow icon={MessageSquare} label="Send Payment Reminder" onClick={sendPaymentReminderInline} disabled={busy || isCancelled} testid={`qa-send-reminder-${b.booking_id}`} />
      )}
      <ActionRow icon={CreditCard}    label="Payment"                  onClick={openPayment}       disabled={busy} testid={`qa-payment-${b.booking_id}`} />
      <ActionRow icon={FileText}      label="Invoice"                  onClick={openInvoice}       disabled={busy} testid={`qa-invoice-${b.booking_id}`} />
      <ActionRow icon={MessageSquare} label="Messages"                 onClick={openMessages}      disabled={busy} testid={`qa-messages-${b.booking_id}`} />
      <ActionRow icon={Copy}          label="Duplicate Booking Check"  onClick={openDuplicateCheck} disabled={busy} testid={`qa-duplicate-${b.booking_id}`} />
    </div>
  );
}


/* ============ iter110 — Inline action modals ============ */

function ModalShell({ title, subtitle, onClose, children, footer, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid={testid}>
      <div className="w-full max-w-lg max-h-[90vh] bg-white rounded-3xl shadow-2xl flex flex-col overflow-hidden">
        <div className="px-5 py-4 bg-gradient-to-br from-[#0A2E1E] to-[#062818] text-white flex items-start gap-3">
          <div className="flex-1 min-w-0">
            {subtitle && <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-emerald-200/90">{subtitle}</div>}
            <div className="text-lg font-extrabold tracking-tight font-[Outfit] truncate">{title}</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 -mr-1 -mt-1 rounded-full hover:bg-white/10 flex items-center justify-center" data-testid={`${testid}-close`}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
        {footer && <div className="border-t border-gray-100 px-5 py-3 flex items-center justify-end gap-2 bg-gray-50">{footer}</div>}
      </div>
    </div>
  );
}

function BookingActionModal({ kind, booking, customer, onClose, onChanged }) {
  if (kind === "view")            return <ViewModal booking={booking} onClose={onClose} />;
  if (kind === "edit")            return <EditModal booking={booking} onClose={onClose} onSaved={() => { onClose(); onChanged?.(); }} />;
  if (kind === "payment")         return <PaymentModal booking={booking} customer={customer} onClose={onClose} onChanged={onChanged} />;
  if (kind === "invoice")         return <InvoiceModal booking={booking} onClose={onClose} />;
  if (kind === "messages")        return <MessagesModal booking={booking} customer={customer} onClose={onClose} />;
  if (kind === "add-service")     return <AddServiceModal booking={booking} customer={customer} onClose={onClose} onChanged={onChanged} />;
  if (kind === "duplicate-check") return <DuplicateCheckModal booking={booking} onClose={onClose} />;
  return null;
}

function DetailRow({ label, value, mono = false }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 border-b border-gray-100">
      <div className="text-[11px] uppercase tracking-[0.14em] font-extrabold text-gray-500 shrink-0">{label}</div>
      <div className={`text-right text-[13px] font-bold text-gray-900 min-w-0 truncate ${mono ? "font-mono" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}

function ViewModal({ booking, onClose }) {
  const b = booking;
  return (
    <ModalShell title={b.service_name || b.service_key} subtitle="Booking details" onClose={onClose} testid={`view-modal-${b.booking_id}`}
      footer={<Button onClick={onClose} className="rounded-full">Close</Button>}>
      <div className="space-y-1" data-testid={`view-modal-body-${b.booking_id}`}>
        <DetailRow label="Booking ID"     value={b.booking_id} mono />
        <DetailRow label="Status"         value={b.status} />
        <DetailRow label="Payment"        value={b.payment_status} />
        <DetailRow label="Frequency"      value={FREQUENCY_LABEL[b.frequency] || b.frequency} />
        <DetailRow label="Price"          value={`$${Number(b.price || 0).toFixed(2)}`} />
        <DetailRow label="Preferred date" value={b.preferred_date} />
        <DetailRow label="Arrival"        value={b.preferred_time} />
        <DetailRow label="Address"        value={b.address} />
        <DetailRow label="Assigned crew"  value={b.assigned_crew} />
        <DetailRow label="AutoPay"        value={b.autopay_enabled ? "Enabled" : "Disabled"} />
        {b.completed_at && <DetailRow label="Completed at" value={b.completed_at.split("T")[0]} />}
        {b.cancellation_reason && <DetailRow label="Cancellation reason" value={b.cancellation_reason} />}
        {b.notes && (
          <div className="mt-3 rounded-xl bg-gray-50 border border-gray-200 p-3">
            <div className="text-[11px] uppercase tracking-[0.14em] font-extrabold text-gray-500">Notes</div>
            <div className="mt-1 text-[13px] text-gray-800 whitespace-pre-wrap">{b.notes}</div>
          </div>
        )}
      </div>
    </ModalShell>
  );
}

function EditModal({ booking, onClose, onSaved }) {
  const b = booking;
  const [form, setForm] = useState({
    preferred_date: b.preferred_date || "",
    preferred_time: b.preferred_time || "",
    arrival_window: b.arrival_window || (b.preferred_time || "").split("(")[0].trim().toLowerCase() || "",
    frequency: b.frequency || "one_time",
    price: b.price ?? 0,
    notes: b.notes || "",
    payment_method: b.payment_method || "",
    autopay_enabled: !!b.autopay_enabled,
    assigned_crew: b.assigned_crew || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      // Only send fields that changed to keep the diff log clean.
      const patch = {};
      Object.entries(form).forEach(([k, v]) => {
        const orig = b[k] ?? (k === "autopay_enabled" ? false : "");
        if (v !== orig) patch[k] = v;
      });
      if (typeof patch.price === "string") patch.price = parseFloat(patch.price);
      if (Object.keys(patch).length === 0) { toast.info("No changes"); onClose(); return; }
      await api.patch(`/admin/bookings/${b.booking_id}`, patch);
      toast.success("Booking updated");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  return (
    <ModalShell title={`Edit · ${b.service_name || b.service_key}`} subtitle="Update details" onClose={onClose} testid={`edit-modal-${b.booking_id}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-full bg-[#047857] hover:bg-[#065F46]" data-testid={`edit-save-${b.booking_id}`}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
          </Button>
        </>
      }>
      <div className="grid grid-cols-2 gap-3">
        <LabeledInput label="Date" type="date" value={form.preferred_date} onChange={(v) => set({ preferred_date: v })} testid="edit-date" />
        <LabeledSelect label="Arrival window" value={form.arrival_window} onChange={(v) => set({ arrival_window: v })} testid="edit-arrival"
          options={[
            ["morning", "Morning · 7am-11am"], ["midday", "Midday · 11am-2pm"],
            ["afternoon", "Afternoon · 2pm-5pm"], ["evening", "Evening · 5pm-8pm"],
          ]} />
        <LabeledInput label="Time label (legacy)" value={form.preferred_time} onChange={(v) => set({ preferred_time: v })} testid="edit-time" />
        <LabeledSelect label="Frequency" value={form.frequency} onChange={(v) => set({ frequency: v })} testid="edit-frequency"
          options={Object.entries(FREQUENCY_LABEL)} />
        <LabeledInput label="Price ($)" type="number" step="0.01" value={form.price} onChange={(v) => set({ price: v })} testid="edit-price" />
        <LabeledInput label="Assigned crew" value={form.assigned_crew} onChange={(v) => set({ assigned_crew: v })} testid="edit-crew" placeholder="e.g. Crew A" />
        <LabeledSelect label="Payment method" value={form.payment_method} onChange={(v) => set({ payment_method: v })} testid="edit-payment-method"
          options={[
            ["", "—"], ["stripe", "Stripe Checkout"], ["stripe_autopay", "Stripe AutoPay"],
            ["pay_on_arrival", "Pay on arrival"], ["cash", "Cash"], ["invoice", "Invoice"],
          ]} />
        <div className="flex items-center gap-2 mt-6">
          <input type="checkbox" checked={form.autopay_enabled}
                 onChange={(e) => set({ autopay_enabled: e.target.checked })}
                 className="w-4 h-4 accent-[#047857]"
                 data-testid="edit-autopay" />
          <span className="text-[13px] font-extrabold text-gray-800">AutoPay enabled</span>
        </div>
      </div>
      <div className="mt-3">
        <Label className="text-[11px] uppercase tracking-[0.14em] font-extrabold text-gray-500">Notes</Label>
        <textarea rows={3} value={form.notes} onChange={(e) => set({ notes: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-[13px]"
                  data-testid="edit-notes" />
      </div>
    </ModalShell>
  );
}

function LabeledInput({ label, testid, ...props }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-[0.14em] font-extrabold text-gray-500">{label}</Label>
      <Input {...props} onChange={(e) => props.onChange?.(e.target.value)} className="mt-1 h-11 font-extrabold" data-testid={testid} />
    </div>
  );
}

function LabeledSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-[0.14em] font-extrabold text-gray-500">{label}</Label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
              className="mt-1 w-full h-11 rounded-xl border border-gray-200 bg-white px-3 text-[13px] font-extrabold"
              data-testid={testid}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

function PaymentModal({ booking, onClose, onChanged }) {
  const b = booking;
  const [sending, setSending] = useState(false);
  const [timeline, setTimeline] = useState(null);
  const [loadingTimeline, setLoadingTimeline] = useState(true);
  const isPaid = (b.payment_status || "").toLowerCase() === "paid";

  useEffect(() => {
    let active = true;
    (async () => {
      setLoadingTimeline(true);
      try {
        const { data } = await api.get(`/admin/bookings/${b.booking_id}/payment-timeline`);
        if (active) setTimeline(data);
      } catch {
        if (active) setTimeline({ timeline: [] });
      } finally {
        if (active) setLoadingTimeline(false);
      }
    })();
    return () => { active = false; };
  }, [b.booking_id]);

  const sendReminder = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/admin/bookings/${b.booking_id}/payment-reminder`, {});
      const parts = [];
      if (data.channels_sent?.length) parts.push(`sent via ${data.channels_sent.join(" + ")}`);
      if (data.channels_failed?.length) parts.push(`failed on ${data.channels_failed.join(", ")}`);
      toast.success(`Payment reminder ${parts.join(" · ") || "queued"}`);
      // refetch timeline
      const { data: t } = await api.get(`/admin/bookings/${b.booking_id}/payment-timeline`);
      setTimeline(t);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send reminder");
    } finally { setSending(false); }
  };
  const resendReceipt = async () => {
    setSending(true);
    try {
      await api.post(`/admin/bookings/${b.booking_id}/resend-receipt`, {});
      toast.success("Receipt re-sent to customer");
      const { data: t } = await api.get(`/admin/bookings/${b.booking_id}/payment-timeline`);
      setTimeline(t);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resend receipt");
    } finally { setSending(false); }
  };
  const copyLink = () => {
    const url = b.checkout_url || `${window.location.origin}/receipt/${b.booking_id}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Payment link copied"));
  };
  const viewReceipt     = () => window.open(`/receipt/${b.booking_id}`, "_blank", "noopener");
  const downloadReceipt = () => window.open(`/receipt/${b.booking_id}?print=1`, "_blank", "noopener");

  return (
    <ModalShell title="Payment" subtitle={b.service_name || b.service_key} onClose={onClose} testid={`payment-modal-${b.booking_id}`}
      footer={<Button onClick={onClose} className="rounded-full">Close</Button>}>
      <div className="space-y-1" data-testid={`payment-modal-body-${b.booking_id}`}>
        <DetailRow label="Amount"          value={`$${Number(b.price || 0).toFixed(2)}`} />
        <DetailRow label="Payment status"  value={b.payment_status} />
        <DetailRow label="Method"          value={b.payment_method || (b.customer_has_card_on_file ? `${b.customer_card_brand || "card"} · ${b.customer_card_last4 || ""}`.trim() : "—")} />
        <DetailRow label="AutoPay"         value={(b.autopay_enabled || b.customer_autopay_enabled) ? "Enabled" : "Disabled"} />
        <DetailRow label="Invoice ID"      value={b.invoice_id} mono />
        {b.paid_at && <DetailRow label="Paid at" value={b.paid_at.split("T")[0]} />}
      </div>

      {/* iter112 — Action row: unpaid vs paid presents different affordances. */}
      {!isPaid ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={copyLink} className="rounded-full" data-testid={`payment-copy-link-${b.booking_id}`}>
              <Copy className="w-4 h-4 mr-1" /> Copy pay link
            </Button>
            <Button onClick={sendReminder} disabled={sending} className="rounded-full bg-[#047857] hover:bg-[#065F46]" data-testid={`payment-send-reminder-${b.booking_id}`}>
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><MessageSquare className="w-4 h-4 mr-1" /> Send reminder</>}
            </Button>
          </div>
          {/* iter113 — Retry Charge for payment_failed / payment_pending with a card on file */}
          <RetryChargeRow booking={b} onChanged={onChanged} setSending={setSending} sending={sending}
                          refreshTimeline={async () => {
                            try {
                              const { data: t } = await api.get(`/admin/bookings/${b.booking_id}/payment-timeline`);
                              setTimeline(t);
                            } catch { /* noop */ }
                          }} />
        </>
      ) : (
        <div className="mt-4 grid grid-cols-3 gap-2">
          <Button variant="outline" onClick={viewReceipt} className="rounded-full" data-testid={`payment-view-receipt-${b.booking_id}`}>
            <FileText className="w-4 h-4 mr-1" /> View
          </Button>
          <Button variant="outline" onClick={downloadReceipt} className="rounded-full" data-testid={`payment-download-receipt-${b.booking_id}`}>
            <FileText className="w-4 h-4 mr-1" /> PDF
          </Button>
          <Button onClick={resendReceipt} disabled={sending} className="rounded-full bg-[#047857] hover:bg-[#065F46]" data-testid={`payment-resend-receipt-${b.booking_id}`}>
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4 mr-1" /> Email again</>}
          </Button>
        </div>
      )}

      {/* iter112 — Payment timeline */}
      <div className="mt-5 rounded-2xl border border-gray-200 bg-gray-50 p-3" data-testid={`payment-timeline-${b.booking_id}`}>
        <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-gray-500 mb-2">Payment timeline</div>
        {loadingTimeline ? (
          <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (timeline?.timeline || []).length === 0 ? (
          <div className="text-sm text-gray-500">No activity yet. Actions above will populate this timeline.</div>
        ) : (
          <ol className="space-y-1.5" data-testid={`payment-timeline-list-${b.booking_id}`}>
            {timeline.timeline.map((e, i) => (
              <li key={i} className="flex items-start gap-2 text-[12px]">
                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-extrabold text-gray-900">{TIMELINE_LABEL[e.kind] || e.kind.replace(/_/g, " ")}</div>
                  <div className="text-gray-500 tabular-nums">{(e.at || "").replace("T", " · ").slice(0, 16)}</div>
                  {e.kind === "payment_reminder_sent" && e.meta?.channels_sent?.length > 0 && (
                    <div className="text-[11px] text-emerald-800">via {e.meta.channels_sent.join(" + ")}</div>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </ModalShell>
  );
}

const TIMELINE_LABEL = {
  payment_reminder_sent: "Payment reminder sent",
  payment_link_opened:   "Customer opened payment link",
  payment_completed:     "Payment completed",
  receipt_emailed:       "Receipt emailed to customer",
  receipt_viewed:        "Receipt viewed by customer",
  receipt_downloaded:    "Receipt downloaded by customer",
  receipt_resent:        "Receipt re-sent by admin",
  retry_charge_attempt:  "Retry charge attempted",
};


function RetryChargeRow({ booking, onChanged, refreshTimeline, sending, setSending }) {
  const b = booking;
  const ps = (b.payment_status || "").toLowerCase();
  const eligible = ["payment_failed", "payment_pending"].includes(ps);
  const hasCard = !!b.customer_has_card_on_file;
  const attempts = Number(b.auto_charge_attempts || 0);
  const history = Array.isArray(b.charge_history) ? b.charge_history : [];
  const [open, setOpen] = useState(false);

  if (!eligible) return null;

  // iter209 — Owner directive (2026-03-01): use the uncapped
  // `/retry-payment` endpoint (not `/retry-charge`) so admin can retry
  // as many times as needed. Idempotency-keyed by attempt number, so a
  // double-click or race with the automatic retry loop collapses to a
  // single Stripe charge.
  const retry = async () => {
    if (!window.confirm(`Retry charging the customer's saved card for $${Number(b.price || 0).toFixed(2)}?\n\nAttempt #${attempts + 1}. No cap — you can retry again later if this attempt fails.`)) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/bookings/${b.booking_id}/retry-payment`, {});
      if (data.ok) {
        toast.success(`Payment captured — $${Number(b.price || 0).toFixed(2)} (Stripe PI ${data.payment_intent_id || ""})`);
      } else {
        toast.error(`Retry failed: ${data.charge_info || "Unknown error"}. Click Retry Payment again after fixing the card.`);
      }
      await refreshTimeline?.();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally { setSending(false); }
  };

  return (
    <div className="mt-3 rounded-2xl border-2 border-rose-200 bg-rose-50/50 p-3" data-testid={`retry-charge-row-${b.booking_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-rose-800 flex items-center gap-1">
            Completed · payment pending
          </div>
          <div className="mt-0.5 text-xs text-rose-900">
            {attempts > 0 ? `${attempts} attempt${attempts === 1 ? "" : "s"} used.` : "No attempts yet."}
            {!hasCard && " Customer has no saved card — use Send Payment Reminder instead."}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {history.length > 0 && (
            <Button variant="ghost" onClick={() => setOpen((v) => !v)} className="rounded-full text-xs"
                    data-testid={`retry-history-toggle-${b.booking_id}`}>
              {open ? "Hide" : "Show"} history ({history.length})
            </Button>
          )}
          <Button onClick={retry} disabled={sending || !hasCard}
                  className="rounded-full bg-rose-700 hover:bg-rose-800 text-white text-xs"
                  data-testid={`retry-payment-btn-${b.booking_id}`}>
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Retry Payment"}
          </Button>
        </div>
      </div>
      {open && history.length > 0 && (
        <ol className="mt-2 space-y-1 border-t border-rose-100 pt-2" data-testid={`retry-history-list-${b.booking_id}`}>
          {history.map((h, i) => (
            <li key={i} className="text-[11px] flex items-start gap-2">
              <span className={`mt-0.5 w-1.5 h-1.5 rounded-full shrink-0 ${h.ok ? "bg-emerald-500" : "bg-rose-500"}`} />
              <div className="flex-1 min-w-0">
                <div className="font-bold text-gray-900">
                  Attempt #{h.attempt_number} · {h.ok ? "SUCCESS" : "FAILED"} · {h.actor === "admin" ? "manual" : "auto"}
                </div>
                <div className="text-gray-500 tabular-nums">{(h.at || "").replace("T", " · ").slice(0, 16)}</div>
                <div className="text-gray-700 break-all">{h.info}</div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function InvoiceModal({ booking, onClose }) {
  const b = booking;
  const hasInvoice = !!b.invoice_id;
  return (
    <ModalShell title="Invoice" subtitle={b.service_name || b.service_key} onClose={onClose} testid={`invoice-modal-${b.booking_id}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          {hasInvoice && (
            <Button
              onClick={() => window.open(`/invoice/${b.invoice_id}`, "_blank", "noopener")}
              className="rounded-full bg-[#047857] hover:bg-[#065F46]"
              data-testid={`invoice-open-${b.booking_id}`}
            >
              <FileText className="w-4 h-4 mr-1" /> Open invoice
            </Button>
          )}
        </>
      }>
      {hasInvoice ? (
        <div className="space-y-1">
          <DetailRow label="Invoice ID" value={b.invoice_id} mono />
          <DetailRow label="Amount"     value={`$${Number(b.price || 0).toFixed(2)}`} />
          <DetailRow label="Status"     value={b.payment_status} />
          <p className="mt-3 text-sm text-gray-600">Opens the invoice in a new tab so you don&apos;t lose your place on this page.</p>
        </div>
      ) : (
        <p className="text-sm text-gray-600">No invoice has been generated yet. Invoices are auto-created when the visit is marked <b>Complete</b>.</p>
      )}
    </ModalShell>
  );
}

function MessagesModal({ booking, customer, onClose }) {
  const b = booking;
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const smsHref = customer?.phone ? `sms:${customer.phone.replace(/\D/g, "")}?body=${encodeURIComponent(`Hi ${customer.name || ""}, `)}` : null;
  const emailHref = customer?.email ? `mailto:${customer.email}?subject=${encodeURIComponent(`About your ${b.service_name || "service"} visit`)}` : null;

  const send = async () => {
    if (!msg.trim()) return;
    setSending(true);
    try {
      await api.post(`/admin/customers/${b.user_id}/message`, { channel: "sms", body: msg });
      toast.success("Message sent");
      setMsg("");
    } catch (e) {
      if (e?.response?.status === 404) toast.info("Direct-send endpoint not enabled — use SMS / Email buttons below.");
      else toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <ModalShell title="Send a message" subtitle={customer?.name || "customer"} onClose={onClose} testid={`messages-modal-${b.booking_id}`}
      footer={<Button onClick={onClose} className="rounded-full">Close</Button>}>
      <div className="space-y-3">
        <div className="flex gap-2">
          {smsHref && (
            <a href={smsHref} className="flex-1" data-testid={`messages-sms-${b.booking_id}`}>
              <Button variant="outline" className="w-full rounded-full"><MessageSquare className="w-4 h-4 mr-1" /> SMS</Button>
            </a>
          )}
          {emailHref && (
            <a href={emailHref} className="flex-1" data-testid={`messages-email-${b.booking_id}`}>
              <Button variant="outline" className="w-full rounded-full"><Mail className="w-4 h-4 mr-1" /> Email</Button>
            </a>
          )}
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-[0.14em] font-extrabold text-gray-500">Quick SMS</Label>
          <textarea rows={3} value={msg} onChange={(e) => setMsg(e.target.value)}
                    placeholder={`Hi ${customer?.name || ""}, quick update on your ${b.service_name || "service"}...`}
                    className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-[13px]"
                    data-testid={`messages-textarea-${b.booking_id}`} />
          <Button onClick={send} disabled={sending || !msg.trim()} className="mt-2 rounded-full bg-[#047857] hover:bg-[#065F46]" data-testid={`messages-send-${b.booking_id}`}>
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send"}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

function AddServiceModal({ booking, customer, onClose }) {
  const b = booking;
  const bookHref = `/book?service=lawn_mowing&for=${customer?.user_id || ""}&ref=admin-add-service`;
  return (
    <ModalShell title="Add a service" subtitle={customer?.name || "customer"} onClose={onClose} testid={`addservice-modal-${b.booking_id}`}
      footer={<Button onClick={onClose} className="rounded-full">Close</Button>}>
      <p className="text-sm text-gray-700">Open the booking flow pre-filled for this customer to add a new service in its own step-by-step Add Service dialog:</p>
      <a href={bookHref} target="_blank" rel="noopener noreferrer" data-testid={`addservice-open-book-${b.booking_id}`}>
        <Button className="mt-3 w-full rounded-full bg-[#047857] hover:bg-[#065F46]">
          <PackagePlus className="w-4 h-4 mr-1" /> Open booking flow for {customer?.name || "customer"}
        </Button>
      </a>
      <p className="mt-3 text-xs text-gray-500">Full &quot;Add Service&quot; from the Customer Bookings page (with same-day auto-combine + separate invoices) ships in Phase 3.</p>
    </ModalShell>
  );
}

function DuplicateCheckModal({ booking, onClose }) {
  const b = booking;
  const [dupes, setDupes] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        // Simple client-side dupe scan against this customer's own bookings
        const { data } = await api.get(`/admin/customers/${b.user_id}/bookings`);
        if (!active) return;
        const others = (data.bookings || []).filter((x) => x.booking_id !== b.booking_id);
        const address = (b.address || "").toLowerCase().trim();
        const date = b.preferred_date || "";
        const svc = b.service_key || "";
        const matches = others.filter((x) => {
          const sameAddr = (x.address || "").toLowerCase().trim() === address && address;
          const sameDate = (x.preferred_date || "") === date && date;
          const sameSvc  = (x.service_key || "") === svc;
          return sameAddr && sameDate && sameSvc;
        });
        setDupes(matches);
      } catch {
        if (active) setDupes([]);
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [b.booking_id, b.user_id, b.address, b.preferred_date, b.service_key]);

  return (
    <ModalShell title="Duplicate booking check" subtitle={b.service_name || b.service_key} onClose={onClose} testid={`dup-modal-${b.booking_id}`}
      footer={<Button onClick={onClose} className="rounded-full">Close</Button>}>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-600"><Loader2 className="w-4 h-4 animate-spin" /> Scanning bookings…</div>
      ) : dupes.length === 0 ? (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-900 font-bold" data-testid="dup-none">
          <CheckCircle2 className="w-4 h-4 inline mr-1" />
          No duplicates found for this address + date + service.
        </div>
      ) : (
        <div className="space-y-2" data-testid="dup-list">
          <p className="text-sm text-rose-800 font-bold">{dupes.length} possible duplicate{dupes.length === 1 ? "" : "s"} found:</p>
          {dupes.map((d) => (
            <div key={d.booking_id} className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-[13px]">
              <div className="font-extrabold">{d.service_name}</div>
              <div className="font-mono text-xs text-gray-500">{d.booking_id}</div>
              <div className="text-xs mt-1">{d.preferred_date} · {d.status} · ${Number(d.price || 0).toFixed(2)}</div>
            </div>
          ))}
        </div>
      )}
    </ModalShell>
  );
}

